import { useState, useEffect, useRef } from 'react';
import { useApi } from '../services/api';
import { useCity } from '../contexts/CityContext';
import styles from './StopsPage.module.css';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from '../assets/icons/bus-stop-marker.svg';
import userLocationIcon from '../assets/icons/user-location.svg';
import LineChipsGroup from '../components/common/LineChipsGroup';
import { useNavigate } from 'react-router-dom';
import { MdInfo } from 'react-icons/md';

// Marker icon oluşturma
const busStopIcon = L.icon({
  iconUrl: markerIcon,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const userIcon = L.icon({
  iconUrl: userLocationIcon,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
});

// API'den gelen durak türü
interface BusStop {
  bus_stop_id: number;
  bus_stop_name: string;
  latitude: number;
  longitude: number;
  active: boolean;
  serving_bus_lines: string[];
  distance?: number; // Kullanıcıya olan mesafe (opsiyonel)
}

// Harita merkezini güncelleyen bileşen
const MapCenterHandler = ({ center, zoom }: { center: [number, number], zoom: number }) => {
  const map = useMap();
  
  useEffect(() => {
    map.setView(center, zoom, { animate: true });
  }, [map, center, zoom]);
  
  return null;
};

// Haritada görünecek sınırları belirleyen bileşen
const MapBoundsHandler = ({ stops, userLocation, isInitialLoad }: { 
  stops: BusStop[], 
  userLocation: [number, number] | null,
  isInitialLoad: boolean
}) => {
  const map = useMap();
  
  useEffect(() => {
    // Sadece ilk yüklemede veya durak listesi değiştiğinde sınırları ayarlayalım
    if (!isInitialLoad) return;
    
    if (stops.length === 0 && !userLocation) return;
    
    if (stops.length === 0 && userLocation) {
      map.setView(userLocation, 15);
      return;
    }
    
    const bounds = L.latLngBounds([]);
    
    // Durakları sınırlara ekle
    stops.forEach(stop => {
      bounds.extend([stop.latitude, stop.longitude]);
    });
    
    // Kullanıcı konumunu sınırlara ekle
    if (userLocation) {
      bounds.extend(userLocation);
    }
    
    // Sınırları ayarla ve padding ekle
    map.fitBounds(bounds, { padding: [50, 50] });
  }, [map, stops, userLocation, isInitialLoad]);
  
  return null;
};

// Harita olaylarını dinleyen ve görünüm değişikliklerini yakalayan bileşen
const MapEventHandler = ({ onViewportChange }: { onViewportChange: (center: [number, number], zoom: number) => void }) => {
  const map = useMapEvents({
    moveend: () => {
      const center = map.getCenter();
      onViewportChange([center.lat, center.lng], map.getZoom());
    },
    zoomend: () => {
      const center = map.getCenter();
      onViewportChange([center.lat, center.lng], map.getZoom());
    }
  });
  
  return null;
};

function StopsPage() {
  const { currentCity, loading: cityLoading } = useCity();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [stops, setStops] = useState<BusStop[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number] | null>(null);
  const [mapZoom, setMapZoom] = useState<number>(13);
  const [nearbyStops, setNearbyStops] = useState<BusStop[]>([]);
  const [visibleStops, setVisibleStops] = useState<BusStop[]>([]);
  const [isMapLoaded, setIsMapLoaded] = useState<boolean>(false);
  const [showStopsOnMap, setShowStopsOnMap] = useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const MIN_ZOOM_FOR_STOPS = 14; // Durakların görünmeye başlayacağı minimum zoom seviyesi
  const MAX_VISIBLE_STOPS = 50; // Haritada gösterilecek maksimum durak sayısı
  const CACHE_EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24 saat (milisaniye cinsinden)
  const api = useApi();
  const mapRef = useRef<L.Map | null>(null);
  const stopsCache = useRef<{[cityCode: string]: {data: BusStop[], timestamp: number}}>({});
  const navigate = useNavigate();

  // Leaflet varsayılan ikonlarını düzelt
  useEffect(() => {
    // Leaflet varsayılan ikonlarını düzelt
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
      iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png'
    });
  }, []);

  // Kullanıcının konumunu alma
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation([latitude, longitude]);
          setMapCenter([latitude, longitude]);
        },
        (error) => {
          console.warn('Konum alınamadı:', error.message);
          // Konum alınamazsa varsayılan konum veya şehir merkezi
          if (currentCity?.location?.latitude !== undefined && currentCity?.location?.longitude !== undefined) {
            setMapCenter([currentCity.location.latitude, currentCity.location.longitude]);
          } else {
            // Varsayılan bir konum kullan (İstanbul merkezi)
            setMapCenter([41.0082, 28.9784]);
          }
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 10000 }
      );
    } else {
      // Tarayıcı geolocation desteklemiyorsa varsayılan bir konum kullan
      setMapCenter([41.0082, 28.9784]); // İstanbul merkezi
    }
  }, [currentCity]);

  // Verilerin güncel olup olmadığını kontrol eden fonksiyon
  const isDataFresh = (timestamp: number): boolean => {
    const now = new Date().getTime();
    return (now - timestamp) < CACHE_EXPIRY_TIME;
  };

  // Durakları yükleme
  useEffect(() => {
    let isMounted = true;
      const fetchStops = async () => {
      // Şehir yükleme işlemi devam ediyorsa bekleyelim
      if (cityLoading) {
        setLoading(true);
        return; // Şehir yüklenene kadar işlem yapmayalım
      }
      
      if (!currentCity) {
        console.log('Şehir seçili olmadığı için duraklar yüklenemiyor');
        setError('Lütfen önce bir şehir seçin.');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);        // Memory cache kontrolü
        if (stopsCache.current[currentCity.cityCode]) {
          const cachedData = stopsCache.current[currentCity.cityCode];
          if (isDataFresh(cachedData.timestamp)) {
            console.log('Durak verileri memory cache\'den yükleniyor...');
            setStops(cachedData.data);
            setLoading(false);
            return;
          }
        }
        
        // Cache'de veri yok veya süresi dolmuş, API isteği yap
        console.log('Cache bulunamadı veya süresi dolmuş. API isteği yapılıyor...');
        
        const params: { search?: string } = {};
        if (searchTerm) {
          params.search = searchTerm;
        }
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 20000); // 20 saniye timeout
        
        try {
          const response = await api.getBusStops(params);
          
          if (!isMounted) return;
          
          if (response && response.status === "success" && response.data) {
            // Verileri optimize et - gereksiz alanları temizle
            // API'den gelen yanıt için ayrı bir interface tanımla
            interface BusStopResponse {
              bus_stop_id: number;
              bus_stop_name: string;
              latitude: number;
              longitude: number;
              active: boolean;
              serving_bus_lines: string[];
              // createdAt ve updatedAt gibi diğer alanlar da olabilir
            }

            const optimizedData: BusStop[] = (response.data as BusStopResponse[]).map(stop => ({
              bus_stop_id: stop.bus_stop_id,
              bus_stop_name: stop.bus_stop_name,
              latitude: stop.latitude,
              longitude: stop.longitude,
              active: stop.active,
              serving_bus_lines: stop.serving_bus_lines
              // createdAt ve updatedAt gibi gereksiz alanlar alınmadı
            }));
            
            console.log('Duraklar başarıyla yüklendi, toplam:', optimizedData.length);
            
            setStops(optimizedData);
              // Verileri sadece memory cache'e kaydet
            const timestamp = new Date().getTime();
            stopsCache.current[currentCity.cityCode] = { data: optimizedData, timestamp };
          } else {
            setStops([]);
            console.warn('API yanıtında data bulunamadı');
            setError('Sunucudan geçerli bir yanıt alınamadı. Lütfen daha sonra tekrar deneyin.');
          }
        } catch (fetchErr: any) {
          if (!isMounted) return;
          
          if (fetchErr.name === 'AbortError') {
            setError('Sunucu yanıt vermedi. İstek zaman aşımına uğradı.');
          } else {
            throw fetchErr;
          }
        } finally {
          clearTimeout(timeoutId);
        }
      } catch (err: any) {
        if (!isMounted) return;
        
        console.error('Duraklar yüklenirken hata oluştu:', err);
        
        let errorMessage = 'Durak bilgileri yüklenirken bir sorun oluştu.';
        
        if (err.message && err.message.includes('Failed to fetch')) {
          errorMessage = 'Sunucuya bağlanılamadı. Lütfen internet bağlantınızı kontrol edin ve tekrar deneyin.';
        } else if (err.message && err.message.includes('404')) {
          errorMessage = 'Durak bilgileri bulunamadı. Seçili şehir için bu hizmet henüz mevcut olmayabilir.';
        } else if (err.message && err.message.includes('500')) {
          errorMessage = 'Sunucuda bir hata oluştu. Teknik ekibimiz sorunu çözmek için çalışıyor. Lütfen daha sonra tekrar deneyin.';
        }
        
        setError(errorMessage);
        setStops([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };    fetchStops();
    
    return () => {
      isMounted = false;
    };
  }, [api, cityLoading, currentCity]);

  // Harita görünümü değiştiğinde yakındaki durakları hesaplama
  useEffect(() => {
    if (!mapCenter || !isMapLoaded || stops.length === 0) return;
    
    console.log('Durak verileri filtreleniyor ve mesafeler hesaplanıyor...');
    
    // Önce harita merkezine yakın durakları filtreleme - performans için önemli
    // Harita merkezinde 10km yarıçapındaki durakları al
    const MAX_DISTANCE_TO_SHOW = 10; // km
    const roughFilteredStops = stops.filter(stop => {
      const distance = calculateDistance(mapCenter[0], mapCenter[1], stop.latitude, stop.longitude);
      return distance <= MAX_DISTANCE_TO_SHOW;
    });
    
    console.log(`Kaba filtreleme sonrası durak sayısı: ${roughFilteredStops.length}/${stops.length}`);
    
    // Web Worker kullanılabilir olsaydı daha iyi olurdu, şimdilik setTimeout kullanarak UI bloklanmasını azaltalım
    setTimeout(() => {
      // Harita görünümündeki durakları ve mesafelerini hesaplama
      const stopsWithDistance = roughFilteredStops.map(stop => {
        // Mesafeyi kullanıcı konumuna göre hesapla, kullanıcı konumu yoksa mesafe hesaplama
        const distance = userLocation ? 
          calculateDistance(userLocation[0], userLocation[1], stop.latitude, stop.longitude) : 
          undefined;
        return { ...stop, distance };
      });
      
      // Mesafeye göre sırala ve en yakın 10 durağı al (liste için)
      // Kullanıcı konumu yoksa harita merkezine göre sırala
      const closest = stopsWithDistance
        .sort((a, b) => {
          if (userLocation) {
            // Kullanıcı konumu varsa, mesafeye göre sırala
            return (a.distance || Infinity) - (b.distance || Infinity);
          } else {
            // Kullanıcı konumu yoksa, harita merkezine olan mesafeye göre sırala
            const distanceA = calculateDistance(mapCenter[0], mapCenter[1], a.latitude, a.longitude);
            const distanceB = calculateDistance(mapCenter[0], mapCenter[1], b.latitude, b.longitude);
            return distanceA - distanceB;
          }
        })
        .slice(0, 10);
      
      // Harita için görünür durakları belirle (daha küçük alt küme - maksimum MAX_VISIBLE_STOPS)
      const closestForMap = roughFilteredStops.map(stop => {
        const mapDistance = calculateDistance(mapCenter[0], mapCenter[1], stop.latitude, stop.longitude);
        return { ...stop, mapDistance };
      })
        .sort((a, b) => (a.mapDistance || Infinity) - (b.mapDistance || Infinity))
        .slice(0, MAX_VISIBLE_STOPS);
      
      setNearbyStops(closest);
      setVisibleStops(closestForMap);
    }, 0);
  }, [mapCenter, mapZoom, stops, isMapLoaded, userLocation]);

  // İki nokta arasındaki mesafeyi hesaplama (km cinsinden) - daha doğru hesaplama
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Dünya yarıçapı (km)
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    
    // Daha kesin sonuç için 2 ondalık basamağa yuvarla
    return Math.round(distance * 100) / 100;
  };

  // Arama fonksiyonu - localStorage içinde arama yap
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    
    // Eğer yükleme sürüyorsa veya hata varsa arama yapma
    if (loading || error) return;
    
    // Arama terimini kullanarak mevcut durakları filtrele
    if (newSearchTerm.trim() === '') {
      // Arama terimi boşsa ve harita merkezi varsa, yakındaki durakları göster
      if (mapCenter) {
        handleViewportChange(mapCenter, mapZoom);
      }
    } else {
      // Tüm duraklarda lokal arama yap
      const searchTermLower = newSearchTerm.toLowerCase();
      
      const filteredStops = stops.filter(stop => 
        stop.bus_stop_name.toLowerCase().includes(searchTermLower) || 
        stop.bus_stop_id.toString().includes(searchTermLower) ||
        stop.serving_bus_lines.some(line => line.toLowerCase().includes(searchTermLower))
      ).slice(0, 10); // Sadece ilk 10 sonucu göster
      
      setNearbyStops(filteredStops);
      
      // Eğer arama sonuçları haritada görünecekse, görünür durakları güncelle
      if (showStopsOnMap) {
        setVisibleStops(filteredStops.slice(0, MAX_VISIBLE_STOPS));
      }
    }
  };

  // Harita yüklendiğinde çağrılacak
  const handleMapLoad = () => {
    setIsMapLoaded(true);
    // Harita yüklendiğinde eğer merkez ve zoom değeri varsa, görünümü güncelle
    if (mapCenter && mapRef.current) {
      // Yakındaki durakları hesapla ve görüntüle
      handleViewportChange(mapCenter, mapZoom);
    }
  };

  // Harita görünümü değiştiğinde çağrılacak
  const handleViewportChange = (center: [number, number], zoom: number) => {
    console.log('Harita görünümü değişti:', center, zoom);
    
    // Kullanıcı haritayı hareket ettirdiğinde, artık ilk yükleme değil
    if (isInitialLoad) setIsInitialLoad(false);
    
    setMapCenter(center);
    setMapZoom(zoom);
    
    // Yakınlaştırma seviyesi kontrol ediliyor
    const shouldShowStops = zoom >= MIN_ZOOM_FOR_STOPS;
    setShowStopsOnMap(shouldShowStops);
    
    // Yakınlaştırma seviyesi uygun değilse, durakları gizle
    if (!shouldShowStops) {
      setVisibleStops([]);
      return;
    }
    
    // Eğer duraklar mevcutsa ve isMapLoaded true ise durakları göster
    if (stops.length > 0 && center && isMapLoaded) {
      updateVisibleStops(center, zoom);
    }
  };

  // Yeni fonksiyon: Görünür durakları güncelleme
  const updateVisibleStops = (center: [number, number], zoom: number) => {
    console.log('Görünür duraklar güncelleniyor...');
    
    // Önce harita merkezine yakın durakları filtrele (performans için)
    const MAX_DISTANCE_FOR_DISPLAY = zoom < 16 ? 5 : 2; // km - zoom seviyesine göre dinamik mesafe
    
    const nearbyForFiltering = stops.filter(stop => {
      const distanceToCenter = calculateDistance(center[0], center[1], stop.latitude, stop.longitude);
      return distanceToCenter <= MAX_DISTANCE_FOR_DISPLAY;
    });
    
    console.log(`Filtrelenen durak sayısı: ${nearbyForFiltering.length}/${stops.length}`);
    
    // Harita merkezi için görünür durakları hesapla
    const stopsForMap = nearbyForFiltering
      .map(stop => {
        const mapDistance = calculateDistance(center[0], center[1], stop.latitude, stop.longitude);
        return { ...stop, distance: mapDistance }; // mapDistance -> distance olarak değiştirildi
      })
      .sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity))
      .slice(0, MAX_VISIBLE_STOPS);
    
    // Kullanıcı konumu varsa, mesafeleri güncelle
    const stopsWithUserDistance = stopsForMap.map(stop => {
      if (userLocation) {
        const userDist = calculateDistance(userLocation[0], userLocation[1], stop.latitude, stop.longitude);
        return { ...stop, distance: userDist };
      }
      return stop;
    });
    
    setVisibleStops(stopsForMap);
    setNearbyStops(stopsWithUserDistance.slice(0, 15)); // Yakın durakları göster (maksimum 15)
  };

  // Kullanıcı konumuna git butonu
  const goToUserLocation = () => {
    if (userLocation && mapRef.current) {
      mapRef.current.setView(userLocation, 15);
    }
  };

  const handleStopDetails = (stopId: number) => {
    navigate(`/stops/bus/${stopId}`);
  };

  return (
    <div className={styles.stopsPage}>
      <div className={styles.mapContainer}>
        {mapCenter ? (
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            style={{ height: "100%", width: "100%" }}
            ref={(map) => {
              if (map && !mapRef.current) {
                mapRef.current = map;
                handleMapLoad(); // Harita referansı alındığında handleMapLoad'u çağır
              }
            }}
            zoomControl={false} // Zoom kontrollerini kaldır
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {/* Harita merkezi takipçisi */}
            {mapCenter && <MapCenterHandler center={mapCenter} zoom={mapZoom} />}
            
            {userLocation && (
              <Marker position={userLocation} icon={userIcon}>
                <Popup>
                  <strong>Konumunuz</strong>
                </Popup>
              </Marker>
            )}
            
            {showStopsOnMap && visibleStops.map((stop) => (
              <Marker
                key={stop.bus_stop_id}
                position={[stop.latitude, stop.longitude]}
                icon={busStopIcon}
              >
                <Popup>
                  <div className={styles.popupContent}>
                    <strong>{stop.bus_stop_name}</strong>
                    <div>No: {stop.bus_stop_id}</div>
                    {userLocation && stop.distance !== undefined && (
                      <div>
                        <strong>Mesafe:</strong> {stop.distance < 1 
                          ? `${Math.round(stop.distance * 1000)} m` 
                          : `${stop.distance.toFixed(1)} km`}
                      </div>
                    )}
                    {stop.serving_bus_lines && stop.serving_bus_lines.length > 0 && (
                      <div className={styles.servingLines}>
                        <span>Hatlar:</span>
                        <LineChipsGroup 
                          lineNumbers={stop.serving_bus_lines} 
                          size="small" 
                          maxVisible={6} 
                        />
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
            
            <MapBoundsHandler 
              stops={showStopsOnMap ? visibleStops : []} 
              userLocation={userLocation} 
              isInitialLoad={isInitialLoad}
            />
            <MapEventHandler onViewportChange={handleViewportChange} />
            
            {!showStopsOnMap && (
              <div className={`${styles.zoomMessage} leaflet-control`}>
                Yakınlaştır
              </div>
            )}
            
            {showStopsOnMap && visibleStops.length > 0 && (
              <div className={`${styles.infoOverlay} leaflet-control`}>
                {visibleStops.length} durak
                {!userLocation && (
                  <div className={styles.locationWarning}>
                    Konum izni yok
                  </div>
                )}
              </div>
            )}
          </MapContainer>
        ) : (
          <div className={styles.loadingMap}>Harita yükleniyor...</div>
        )}
        
        <div className={styles.mapControls}>
          <button 
            className={styles.locationButton}
            onClick={goToUserLocation}
            title="Konumuma Git"
            disabled={!userLocation}
          >
            <span className={styles.locationIcon}>📍</span>
          </button>
          <button 
            className={styles.zoomButton}
            onClick={() => mapRef.current?.zoomIn()}
          >
            +
          </button>
          <button 
            className={styles.zoomButton}
            onClick={() => mapRef.current?.zoomOut()}
          >
            -
          </button>
        </div>
      </div>
      
      <div className={styles.stopsListOuterContainer}>
        <div className={styles.stopsListHeader}>
          <div className={styles.searchContainer}>
            <input 
              type="text" 
              placeholder="Durak ara..."
              value={searchTerm}
              onChange={handleSearchChange}
              className={styles.searchInput}
            />
          </div>
          
          <h2 className={styles.compactHeading}>Yakın Duraklar</h2>
        </div>
        
        <div className={styles.stopsListContainer}>
          {loading && <div className={styles.loadingMessage}>
            {cityLoading ? 'Şehir bilgileri yükleniyor...' : 'Duraklar yükleniyor...'}
          </div>}
          
          {error && <div className={styles.errorMessage}>{error}</div>}
          
          {!loading && !error && stops.length === 0 && (
            <div className={styles.noResultsMessage}>
              Durak bulunamadı
            </div>
          )}
          
          {!loading && !error && stops.length > 0 && !showStopsOnMap && (
            <div className={styles.infoMessage}>
              Yakınlaştırın
            </div>
          )}
          
          <div className={styles.stopsList}>
            {nearbyStops.map((stop) => (
              <div 
                key={stop.bus_stop_id} 
                className={styles.stopCard}
                onClick={() => {
                  if (mapRef.current) {
                    mapRef.current.setView([stop.latitude, stop.longitude], 17);
                  }
                }}
              >
                <div className={styles.stopInfo}>
                  <div className={styles.stopId}>{stop.bus_stop_id}</div>
                  <div className={styles.stopName}>{stop.bus_stop_name}</div>
                  {userLocation && stop.distance !== undefined && (
                    <div className={styles.stopDistance}>
                      {stop.distance < 1 
                        ? `${Math.round(stop.distance * 1000)} m` 
                        : `${stop.distance.toFixed(1)} km`}
                    </div>
                  )}
                </div>
                
                {stop.serving_bus_lines && stop.serving_bus_lines.length > 0 && (
                  <div className={styles.stopLines}>
                    <LineChipsGroup 
                      lineNumbers={stop.serving_bus_lines} 
                      size="small" 
                      maxVisible={5} 
                    />
                  </div>
                )}
                
                <button
                  className={styles.detailsButton}
                  onClick={(e) => {
                    e.stopPropagation(); // Kartın tıklama olayını durdurun
                    handleStopDetails(stop.bus_stop_id);
                  }}
                  title="Durak detayları"
                >
                  <MdInfo /> Detaylar
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StopsPage;
